/**
 * OCR com IA Generativa (GPT-4 Vision / Claude Vision)
 * Solução para caligrafia complexa manuscrita
 */

/**
 * Converte imagem para base64
 */
export const imagemParaBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      // Remover prefixo data:image/...;base64,
      const base64 = reader.result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

/**
 * Processa card com GPT-4 Vision (via backend)
 */
export const processarCardComIA = async (imagemFile, api) => {
  try {
    // Converter imagem para base64
    const base64 = await imagemParaBase64(imagemFile);

    // Enviar para backend processar com IA
    const response = await api.post('/ocr/processar-card-ia', {
      imagem: base64,
      tipo: 'auto' // Cascata automática: Gemini → Groq → OpenAI
    });

    if (response.data.sucesso) {
      return {
        sucesso: true,
        dados: response.data.dados,
        confianca: response.data.confianca || 80,
        metodo: response.data.metodo || 'IA Generativa'
      };
    }

    throw new Error(response.data.erro || 'Erro ao processar com IA');

  } catch (error) {
    console.error('Erro processamento IA:', error);
    return {
      sucesso: false,
      erro: error.message,
      dados: null
    };
  }
};

/**
 * Processa card com múltiplos métodos (cascata)
 * Tenta IA primeiro, fallback para Tesseract
 */
export const processarCardCascata = async (imagemFile, api, Tesseract, setProgresso) => {
  console.log('🚀 Iniciando processamento em cascata...');

  // MÉTODO 1: Tentar IA Generativa (se disponível)
  try {
    setProgresso && setProgresso(10);
    console.log('🤖 Tentando IA (Gemini/Groq/GPT-4)...');

    const resultadoIA = await processarCardComIA(imagemFile, api);

    if (resultadoIA.sucesso) {
      setProgresso && setProgresso(100);
      console.log('✅ IA: Sucesso!', resultadoIA);
      return resultadoIA;
    }

    console.warn('⚠️ IA falhou, tentando fallback...');
  } catch (error) {
    console.warn('⚠️ IA indisponível:', error.message);
  }

  // MÉTODO 2: Fallback para Tesseract com pré-processamento múltiplo
  try {
    setProgresso && setProgresso(30);
    console.log('📝 Processando com Tesseract.js (múltiplos thresholds)...');

    // Processar com diferentes thresholds
    const thresholds = [120, 140, 160, 180];
    const resultados = [];

    for (const threshold of thresholds) {
      setProgresso && setProgresso(30 + (thresholds.indexOf(threshold) * 15));

      const imagemProcessada = await preprocessImageWithThreshold(imagemFile, threshold);
      
      const { data: { text, confidence } } = await Tesseract.recognize(
        imagemProcessada,
        'por',
        {
          logger: m => {
            if (m.status === 'recognizing text') {
              const progress = 30 + Math.round(m.progress * 60);
              setProgresso && setProgresso(progress);
            }
          }
        }
      );

      resultados.push({
        threshold,
        texto: text,
        confianca: confidence
      });
    }

    // Escolher melhor resultado
    const melhor = resultados.reduce((best, curr) => 
      curr.confianca > best.confianca ? curr : best
    );

    setProgresso && setProgresso(100);
    console.log('✅ Tesseract: Melhor resultado com threshold', melhor.threshold);

    return {
      sucesso: true,
      dados: melhor.texto,
      confianca: melhor.confianca,
      metodo: `Tesseract (threshold ${melhor.threshold})`
    };

  } catch (error) {
    console.error('❌ Tesseract também falhou:', error);
    return {
      sucesso: false,
      erro: 'Todos os métodos de OCR falharam',
      dados: null
    };
  }
};

/**
 * Pré-processa imagem com threshold específico
 */
const preprocessImageWithThreshold = (imageFile, threshold) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();

    reader.onload = (e) => {
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        const scale = 2;
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;

        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;

        for (let i = 0; i < data.length; i += 4) {
          const gray = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
          const newValue = gray > threshold ? 255 : 0;
          data[i] = newValue;
          data[i + 1] = newValue;
          data[i + 2] = newValue;
        }

        ctx.putImageData(imageData, 0, 0);
        canvas.toBlob((blob) => {
          resolve(new File([blob], imageFile.name, { type: 'image/png' }));
        }, 'image/png', 0.95);
      };

      img.onerror = reject;
      img.src = e.target.result;
    };

    reader.onerror = reject;
    reader.readAsDataURL(imageFile);
  });
};

/**
 * Formata resposta da IA para estrutura esperada pelo sistema
 */
export const formatarRespostaIA = (dadosIA) => {
  return {
    nome: { valor: dadosIA.nome || '', confianca: 85 },
    telefone: { valor: dadosIA.telefone || '', confianca: 80 },
    whatsapp: { valor: dadosIA.whatsapp || dadosIA.telefone || '', confianca: 75 },
    email: { valor: dadosIA.email || '', confianca: 70 },
    cep: { valor: dadosIA.cep || '', confianca: 85 },
    rua: { valor: dadosIA.rua || '', confianca: 75 },
    bairro: { valor: dadosIA.bairro || '', confianca: 80 },
    cidade: { valor: dadosIA.cidade || '', confianca: 90 },
    estado: { valor: dadosIA.estado || '', confianca: 95 },
    sexo: { valor: dadosIA.sexo || '', confianca: 70 },
    checkboxes: {
      quer_ser_membro: dadosIA.quer_ser_membro || false,
      deseja_oracao: dadosIA.deseja_oracao || false,
      quer_conhecer_jesus: dadosIA.quer_conhecer_jesus || false
    }
  };
};
